import { useCallback, useEffect, useState } from "react";
import { Activity, Link2, Plus, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { Empty, StatusPill } from "../components/common";
import { AddMTAccountModal } from "./AddMTAccountModal";
import { api, dateTime, errMsg, usd } from "../lib/api";

export const ctraderRedirectUri = () => `${window.location.origin}/ctrader/callback`;

const AccountCard = ({ a, i, onSync, onDelete }) => (
  <div className="account-card" data-testid={`account-card-${i}`}>
    <div className={`platform-icon ${a.platform === "cTrader" ? "ct" : "mt"}`}>{a.platform === "cTrader" ? "cT" : a.platform}</div>
    <div className="account-meta">
      <b>{a.platform} · {a.broker}</b>
      <small>{a.login} · {a.server} · Sync terakhir {dateTime(a.last_sync)}</small>
      {a.last_error && <small className="down">{a.last_error}</small>}
    </div>
    <div className="account-balance"><b>{usd(a.equity)}</b><small>equity</small></div>
    <StatusPill status={a.status} testId={`account-status-${i}`} />
    <button className="icon-btn" onClick={() => onSync(a)} data-testid={`account-sync-${i}`} title="Sinkronkan"><RefreshCw size={16} /></button>
    <button className="icon-btn" onClick={() => onDelete(a)} data-testid={`account-delete-${i}`} title="Hapus"><Trash2 size={16} /></button>
  </div>
);

export default function Accounts({ notice, refreshOverview }) {
  const [accounts, setAccounts] = useState([]);
  const [status, setStatus] = useState({});
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(notice || "");

  const load = useCallback(() => {
    api.get("/trading/accounts").then((r) => setAccounts(r.data));
    api.get("/brokers/status").then((r) => setStatus(r.data));
  }, []);
  useEffect(load, [load]);

  const sync = async (a) => {
    await api.post(`/trading/accounts/${a.id}/sync`);
    setMessage(`Sinkronisasi ${a.broker} ${a.login} dijalankan di latar belakang.`);
    setTimeout(load, 4000);
  };
  const syncAll = async () => {
    await Promise.all(accounts.map((a) => api.post(`/trading/accounts/${a.id}/sync`)));
    setMessage("Semua akun dijadwalkan untuk sinkronisasi.");
    setTimeout(load, 4000);
  };
  const remove = async (a) => {
    if (!window.confirm(`Hapus akun ${a.broker} ${a.login}? Histori deal akun ini juga dihapus.`)) return;
    await api.delete(`/trading/accounts/${a.id}`);
    load();
    refreshOverview();
  };
  const connectCTrader = async () => {
    try {
      const r = await api.get("/brokers/ctrader/authorize", { params: { redirect_uri: ctraderRedirectUri() } });
      window.location.href = r.data.url;
    } catch (err) {
      setMessage(errMsg(err, "cTrader belum dapat dihubungkan."));
    }
  };

  return (
    <>
      <div className="page-intro">
        <div><p className="eyebrow">BROKER CONNECTIONS</p><h2>Akun trading Anda</h2><p className="muted">Histori deal ditarik otomatis setiap 5 menit.</p></div>
        <div className="row-actions">
          <button className="outline" onClick={connectCTrader} disabled={!status.ctrader_ready} title={status.ctrader_ready ? "" : "Menunggu admin mengaktifkan cTrader Open API"} data-testid="connect-ctrader-button"><Link2 size={16} /> Hubungkan cTrader</button>
          <button className="primary" onClick={() => setOpen(true)} data-testid="accounts-add-button"><Plus size={17} /> Tambah MT4/MT5</button>
        </div>
      </div>
      <div className="connector-strip">
        <div data-testid="metaapi-connector-status"><span className={status.metaapi_ready ? "dot ok" : "dot warn"} /> MetaApi MT4/MT5 · {status.metaapi_ready ? "Aktif" : "Menunggu konfigurasi admin"}</div>
        <div data-testid="ctrader-connector-status"><span className={status.ctrader_ready ? "dot ok" : "dot warn"} /> cTrader Open API · {status.ctrader_ready ? (status.ctrader_authorized ? "Terotorisasi" : "Siap dihubungkan") : "Menunggu konfigurasi admin"}</div>
      </div>
      <div className="notice"><ShieldCheck size={19} /><span><b>Akses read-only.</b> MT4/MT5 memakai password investor, cTrader memakai izin "accounts" — tidak ada akses membuka posisi.</span></div>
      {message && <div className="notice info" data-testid="account-action-message">{message}</div>}
      <section className="panel account-list">
        <div className="panel-head">
          <h3>Akun terhubung <span className="count">{accounts.length}</span></h3>
          <button className="outline" onClick={syncAll} disabled={!accounts.length} data-testid="sync-all-button"><RefreshCw size={15} /> Sync semua</button>
        </div>
        {accounts.length === 0 && <Empty testId="accounts-empty" icon={Activity} title="Belum ada akun" text="Tambahkan akun MT4/MT5 dengan password investor atau hubungkan cTrader." />}
        {accounts.map((a, i) => <AccountCard key={a.id} a={a} i={i} onSync={sync} onDelete={remove} />)}
      </section>
      {open && <AddMTAccountModal metaapiReady={status.metaapi_ready} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); setMessage("Akun tersimpan. Status akan diperbarui otomatis."); load(); refreshOverview(); }} />}
    </>
  );
}
